import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth.jsx";
import { BACKEND_URL } from "@/lib/api";

const ERROR_COPY = {
  domain_restricted:
    "Access restricted to Eyvo employees. Please sign in with your @eyvo.com Google account.",
  email_not_verified:
    "Your Google account doesn't have a verified email. Please verify and try again.",
  oauth_denied: "Sign-in was cancelled.",
  bad_state: "The sign-in link expired. Please try again.",
  missing_params: "The sign-in flow returned without a code. Please try again.",
  invalid_id_token: "We could not verify the Google sign-in. Please try again.",
  token_exchange_failed:
    "We couldn't exchange the Google authorization code. Please try again.",
  missing_id_token: "Google didn't return an ID token. Please try again.",
  oauth_not_configured:
    "Google sign-in isn't configured on the server. Contact an admin.",
};

export default function LoginPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const errorCode = params.get("error");
  const errorMsg = errorCode ? ERROR_COPY[errorCode] || `Sign-in failed (${errorCode}).` : null;

  React.useEffect(() => {
    if (user) navigate("/workflows", { replace: true });
  }, [user, navigate]);

  function handleGoogleSignIn() {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Backend's GET /api/auth/google/login issues 302 to Google with the
    // server-side redirect_uri from OAUTH_REDIRECT_URI.  No client-side
    // redirect URL is involved.
    window.location.href = `${BACKEND_URL}/api/auth/google/login`;
  }

  return (
    <div className="grid min-h-screen grid-cols-1 bg-slate-50 lg:grid-cols-2">
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-slate-900 p-12 text-white lg:flex">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(at 80% 0%, rgba(99,102,241,0.45) 0px, transparent 50%), radial-gradient(at 0% 100%, rgba(79,70,229,0.35) 0px, transparent 50%)",
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-lg bg-indigo-500 shadow-lg shadow-indigo-900/50">
              <ShieldCheck size={20} />
            </div>
            <span className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-300">
              Eyvo Demo Agent
            </span>
          </div>
          <h1 className="mt-16 max-w-md text-4xl font-semibold leading-tight tracking-tight">
            Build, version, and run interactive product demos —
            <span className="text-indigo-300"> reliably, every time.</span>
          </h1>
          <p className="mt-5 max-w-md text-base leading-relaxed text-slate-300">
            Author workflows once, snapshot every change, and let the agent
            drive your live procurement app with screenshots, recordings, and
            narration on the way.
          </p>
        </div>
        <ul className="relative grid grid-cols-3 gap-4 text-[11px] uppercase tracking-wider text-slate-400">
          <li className="border-l-2 border-indigo-500/60 pl-3">Multi-step recording</li>
          <li className="border-l-2 border-indigo-500/60 pl-3">Immutable versions</li>
          <li className="border-l-2 border-indigo-500/60 pl-3">Eyvo SSO</li>
        </ul>
      </aside>

      <section className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <div className="mb-2 flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center overflow-hidden rounded-lg bg-white ring-1 ring-slate-200">
                <img
                  src="/eyvo-logo.jpg"
                  alt="Eyvo"
                  className="h-10 w-10 object-contain"
                />
              </div>
              <span className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-700">
                Demo Agent
              </span>
            </div>
          </div>

          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
            Sign in
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Use your <span className="font-medium text-slate-700">@eyvo.com</span> Google account.
          </p>

          {errorMsg && (
            <Alert variant="destructive" className="mt-6" data-testid="login-error">
              <AlertDescription>
                <div className="flex flex-col gap-2">
                  <span>{errorMsg}</span>
                  {errorCode === "domain_restricted" && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => navigate("/login", { replace: true })}
                      data-testid="try-different-account-button"
                      className="w-fit"
                    >
                      Try a different account
                    </Button>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="mt-8" data-testid="login-form">
            <button
              type="button"
              onClick={handleGoogleSignIn}
              data-testid="google-signin-button"
              className="group flex w-full items-center justify-center gap-3 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-all hover:border-slate-400 hover:shadow active:translate-y-px"
            >
              <GoogleLogo />
              <span>Sign in with Google</span>
            </button>
          </div>

          <p className="mt-6 text-[11px] text-slate-400">
            By signing in you agree to operate the demo agent under the team's
            data-handling rules. Only verified @eyvo.com accounts may proceed.
          </p>
        </div>
      </section>
    </div>
  );
}

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.71H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
